import type { ApiAccountJSON } from './api_types/accounts';

type HideItemsDefinition =
  | 'emoji_reaction_on_timeline'
  | 'emoji_reaction_unavailable_server'
  | 'emoji_reaction_count'
  | 'favourite_menu'
  | 'recent_emojis'
  | 'relationships'
  | 'status_reference_unavailable_server'
  | 'avatar_on_filter';

type InitialStateLanguage = [code: string, name: string, localName: string];

interface InitialStateMeta {
  access_token: string;
  advanced_layout?: boolean;
  auto_play_gif: boolean;
  activity_api_enabled: boolean;
  admin: string;
  boost_modal?: boolean;
  delete_modal?: boolean;
  missing_alt_text_modal?: boolean;
  disable_swiping?: boolean;
  disable_hover_cards?: boolean;
  disabled_account_id?: string;
  display_media: string;
  domain: string;
  expand_spoilers?: boolean;
  limited_federation_mode: boolean;
  locale: string;
  mascot: string | null;
  me?: string;
  moved_to_account_id?: string;
  owner?: string;
  profile_directory: boolean;
  registrations_open: boolean;
  reduce_motion: boolean;
  repository: string;
  search_enabled: boolean;
  trends_enabled: boolean;
  single_user_mode: boolean;
  source_url: string;
  streaming_api_base_url: string;
  timeline_preview: boolean;
  title: string;
  show_trends: boolean;
  trends_as_landing_page: boolean;
  use_blurhash: boolean;
  use_pending_items?: boolean;
  version: string;
  sso_redirect: string;
  status_page_url: string;
  terms_of_service_enabled: boolean;
  emoji_style?: string;
  bookmark_category_needed: boolean;
  community_timeline_instead_of_search_menu: boolean;
  enabled_visibilities: string[];
  dtl_tag: string;
  enable_emoji_reaction: boolean;
  enable_local_timeline: boolean;
  enable_dtl_menu: boolean;
  featured_tags: string[];
  hide_items: HideItemsDefinition[];
  registrations_reach_limit: boolean;
  simple_timeline_menu: boolean;
}

interface Role {
  id: string;
  name: string;
  permissions: string;
  color: string;
  highlighted: boolean;
}

export interface InitialState {
  accounts: Record<string, ApiAccountJSON>;
  languages: InitialStateLanguage[];
  critical_updates_pending?: boolean;
  meta: InitialStateMeta;
  role?: Role;
  features: string[];
}

const element = document.getElementById('initial-state');
export const initialState: InitialState | undefined = element?.textContent
  ? (JSON.parse(element.textContent) as InitialState)
  : undefined;

const initialPath: string =
  document
    .querySelector('head meta[name=initialPath]')
    ?.getAttribute('content') ?? '';
export const hasMultiColumnPath: boolean =
  initialPath === '/' ||
  initialPath === '/getting-started' ||
  initialPath === '/home' ||
  initialPath.startsWith('/deck');

function getMeta<K extends keyof InitialStateMeta>(
  prop: K,
): InitialStateMeta[K] | undefined {
  return initialState?.meta[prop];
}

const hideItems = getMeta('hide_items');
export const isHideItem = (key: HideItemsDefinition): boolean =>
  hideItems?.includes(key) || false;
export const isShowItem = (key: HideItemsDefinition): boolean =>
  !isHideItem(key);

export const activityApiEnabled = getMeta('activity_api_enabled');
export const autoPlayGif = getMeta('auto_play_gif');
export const boostModal = getMeta('boost_modal');
export const deleteModal = getMeta('delete_modal');
export const missingAltTextModal = getMeta('missing_alt_text_modal');
export const disableSwiping = getMeta('disable_swiping');
export const disableHoverCards = getMeta('disable_hover_cards');
export const disabledAccountId = getMeta('disabled_account_id');
export const displayMedia = getMeta('display_media');
export const domain = getMeta('domain');
export const emojiStyle = getMeta('emoji_style') ?? 'auto';
export const expandSpoilers = getMeta('expand_spoilers');
export const forceSingleColumn = !getMeta('advanced_layout');
export const limitedFederationMode = getMeta('limited_federation_mode');
export const mascot = getMeta('mascot');
export const me = getMeta('me');
export const movedToAccountId = getMeta('moved_to_account_id');
export const owner = getMeta('owner');
export const profile_directory = getMeta('profile_directory');
export const reduceMotion = getMeta('reduce_motion');
export const registrationsOpen = getMeta('registrations_open');
export const repository = getMeta('repository');
export const searchEnabled = getMeta('search_enabled');
export const trendsEnabled = getMeta('trends_enabled');
export const showTrends = getMeta('show_trends');
export const singleUserMode = getMeta('single_user_mode');
export const source_url = getMeta('source_url');
export const timelinePreview = getMeta('timeline_preview');
export const title = getMeta('title');
export const trendsAsLanding = getMeta('trends_as_landing_page');
export const useBlurhash = getMeta('use_blurhash');
export const usePendingItems = getMeta('use_pending_items');
export const version = getMeta('version');
export const criticalUpdatesPending = initialState?.critical_updates_pending;
export const statusPageUrl = getMeta('status_page_url');
export const sso_redirect = getMeta('sso_redirect');
export const termsOfServiceEnabled = getMeta('terms_of_service_enabled');
export const bookmarkCategoryNeeded = getMeta('bookmark_category_needed');
export const enabledVisibilites = getMeta('enabled_visibilities');
export const dtlTag = getMeta('dtl_tag');
export const enableEmojiReaction = getMeta('enable_emoji_reaction');
export const enableLocalTimeline = getMeta('enable_local_timeline');
export const enableDtlMenu = getMeta('enable_dtl_menu');
export const featuredTags = getMeta('featured_tags') ?? [];
export const registrationsReachLimit = getMeta('registrations_reach_limit');
export const simpleTimelineMenu = getMeta('simple_timeline_menu');
export const communityTimelineInsteadOfSearchMenu = getMeta(
  'community_timeline_instead_of_search_menu',
);

const displayNames = new Intl.DisplayNames(getMeta('locale'), {
  type: 'language',
  fallback: 'none',
  languageDisplay: 'standard',
});

export const languages = initialState?.languages.map((lang) => {
  // zh-YUE is not a valid CLDR unicode_language_id
  return [
    lang[0],
    displayNames.of(lang[0].replace('zh-YUE', 'yue')) ?? lang[1],
    lang[2],
  ];
});

export function getAccessToken(): string | undefined {
  return getMeta('access_token');
}
